import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { LoyaltyContext } from '../../../../context/loyalty/LoyaltyContext';
import { WalletConfig, WalletProgram } from '../../../../models/interfaces';
import { WalletProgramPanel } from './WalletProgramPanel';
import { PROGRAM_LABEL } from './walletUtils';

const PROGRAMS: WalletProgram[] = ['puntos', 'sellos'];

// Tarjeta digital: diseño de la tarjeta Wallet del comercio (una sola configuración para iPhone y Android).
// Puntos y sellos se configuran por separado; el programa activo queda en la URL (?programa=...).
export const WalletCardTab = () => {
    const { loadWalletConfig } = useContext(LoyaltyContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const requested = searchParams.get('programa') as WalletProgram;
    const active: WalletProgram = PROGRAMS.includes(requested) ? requested : 'puntos';

    const [configs, setConfigs] = useState<WalletConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // Se monta un panel la primera vez que se abre y se conserva, para no perder cambios sin guardar
    const [visited, setVisited] = useState<WalletProgram[]>([active]);

    const reload = async () => {
        const resp = await loadWalletConfig();
        if (resp.error) setError(resp.error);
        else { setError(''); setConfigs(resp.configs ?? []); }
    };

    useEffect(() => {
        reload().then(() => setLoading(false));
    }, []);

    useEffect(() => {
        setVisited(prev => prev.includes(active) ? prev : [...prev, active]);
    }, [active]);

    const select = (tipo: WalletProgram) => {
        const params = new URLSearchParams(searchParams);
        params.set('programa', tipo);
        setSearchParams(params, { replace: true });
    };

    const configOf = (tipo: WalletProgram) => configs.find(c => String(c.tipoPrograma).toLowerCase() === tipo);

    if (loading) {
        return <div className="text-center py-5"><span className="spinner-border spinner-border-sm me-2" />Cargando tarjeta digital...</div>;
    }
    if (error && configs.length === 0) {
        return (
            <div className="alert alert-danger d-flex flex-wrap justify-content-between align-items-center gap-2">
                <span>{error}</span>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => { setLoading(true); reload().then(() => setLoading(false)); }}>
                    Reintentar
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="text-muted small mb-3">
                <i className="bi bi-info-circle me-1" />
                Configure una sola vez la tarjeta que reciben sus clientes en <span className="fw-semibold">Apple Wallet (iPhone)</span> y <span className="fw-semibold">Google Wallet (Android)</span>.
                Al guardar, las tarjetas Android se actualizan solas; en iPhone, cada tarjeta se actualiza en el siguiente movimiento del cliente.
            </div>

            <ul className="nav nav-pills mb-4">
                {PROGRAMS.map(tipo => {
                    const config = configOf(tipo);
                    return (
                        <li className="nav-item" key={tipo}>
                            <button type="button" className={`nav-link ${active === tipo ? 'active' : ''}`} onClick={() => select(tipo)}>
                                <i className={`bi ${tipo === 'sellos' ? 'bi-patch-check' : 'bi-coin'} me-1`} />
                                {PROGRAM_LABEL[tipo]}
                                {config && !config.listoParaUsar && (
                                    <i className={`bi bi-exclamation-circle-fill ms-2 ${active === tipo ? '' : 'text-danger'}`} title="Falta logo o ícono de iPhone" />
                                )}
                            </button>
                        </li>
                    );
                })}
            </ul>

            {PROGRAMS.filter(tipo => visited.includes(tipo)).map(tipo => {
                const config = configOf(tipo);
                return (
                    <div key={tipo} className={active === tipo ? '' : 'd-none'}>
                        {config
                            ? <WalletProgramPanel tipo={tipo} config={config} onReload={reload} />
                            : <div className="alert alert-warning">No se recibió la configuración de {PROGRAM_LABEL[tipo].toLowerCase()}.</div>}
                    </div>
                );
            })}
        </>
    );
};
